import 'reflect-metadata';
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import NotificationPushSubscriptionApi, { type CreateNotificationRegistrationPayload } from '#core/api/operations/NotificationPushSubscription.ts'
import { RegisterServiceWorker } from '#registerServiceworker.ts';
import { UserState } from '#core/state/UserState.ts'
import { StateView } from '#core/state/State.ts'
import './i18n';
import "#assets/style/main.scss"
import "#ynt-design-system/styles.scss"
import "#core/runtime/JSInjections";
import { installTranslatedDomGuard } from '#core/runtime/translated-dom-guard.ts';
import { routeTree } from './routeTree.gen.ts'
import { ServiceWorkerGuard } from '#components/tools/service-worker-guard.tsx';
import { initDiagnostics } from '#core/diagnostics/diagnostics.ts';
import { setTelemetryIdentity } from '#core/diagnostics/telemetry-sender.ts';
// Side-effect import: start capturing `beforeinstallprompt` at module scope,
// before React renders, so the install nudge never misses the (early, single) event.
import '#core/pwa/install-prompt-capture.ts';



// Before the first render: React must never hit a node an in-page translator moved.
installTranslatedDomGuard()

const router = createRouter({ routeTree, })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
  interface StaticDataRouteOption {
    title?: string
    /** Renders the route inside the design-system `AppShell` instead of the Bootstrap shell. */
    appShell?: boolean
  }
}

// Capture cross-tab navigation + JS-error history for bug reports (best-effort).
initDiagnostics(router)
declare global{
    interface Window {activeWorker:ServiceWorkerRegistration;}
}

async function ensurePushRegistration(): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return
  }

  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }

  if (permission !== 'granted') {
    return
  }

  try {
    const notificationsApi = new NotificationPushSubscriptionApi()
    const vapidKeyResponse = await notificationsApi.getVapidPublicKey()
    const vapidKey = vapidKeyResponse?.public_key

    if (!vapidKey) {
      return
    }

    const registration = await RegisterServiceWorker()
    const subscription = await registration.pushManager.getSubscription()
      ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      })
    const existingSubscriptions = await notificationsApi.listPushSubscriptions()
    const isRegistered = existingSubscriptions?.items?.some((item) => item.endpoint === subscription.endpoint) ?? false

    if (!isRegistered) {
      const payload: CreateNotificationRegistrationPayload = {
        endpoint: subscription.endpoint,
        p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
        auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
      }
      await notificationsApi.createPushSubscription(payload)
    }
  } catch (error) {
    console.error('Failed to ensure push notification registration.', error)
  }
}

RegisterServiceWorker().then((sw) => {
  window.activeWorker = sw
})

// A new deploy changes the build hash and purges old chunks; a tab that loads a
// now-missing dynamic import would otherwise white-screen. Reload to pull the
// current build's assets, on a cooldown so a genuinely broken chunk can't loop
// while a later deploy in the same long-lived tab can still recover.
window.addEventListener('vite:preloadError', (e) => {
  if (Date.now() - Number(sessionStorage.getItem('vitePreloadReloadedAt') ?? 0) < 30_000) return
  e.preventDefault()
  sessionStorage.setItem('vitePreloadReloadedAt', String(Date.now()))
  window.location.reload()
})

// The active company can change without a re-login (a super user assuming one, a company
// switch in the URL), so re-stamp on every resolved navigation as well as on login.
function stampTelemetryIdentity(): void {
  const userState = StateView.getInstance(undefined, UserState.name, sessionStorage,
    () => new UserState(UserState.name, sessionStorage))
  setTelemetryIdentity({
    userId: userState.user?.id ?? null,
    companyId: userState.activeCompanyId,
    role: userState.activeRole?.role_name ?? null,
  })
}

let telemetryIdentityTracked = false

// Delay until logged-in so the auth token is in the SW before the push API calls.
// Registering here (not via the UserState singleton) avoids triggering a session
// probe on pages (share links, public forms) that never need the user.
UserState.addOnLoggedInListener(() => void ensurePushRegistration())

// Subscribed from here, not from the diagnostics module: resolving the UserState singleton
// there would start a session probe on pages (share links, public forms) that never need one.
UserState.addOnLoggedInListener(() => {
  stampTelemetryIdentity()
  if (telemetryIdentityTracked) return
  telemetryIdentityTracked = true
  router.subscribe('onResolved', stampTelemetryIdentity)
})

const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement)
  root.render(
    <StrictMode>
      <ServiceWorkerGuard>
        <RouterProvider router={router} />
      </ServiceWorkerGuard>
    </StrictMode>,
  )
}
